import config from '../Config';
import * as Sentry from "@sentry/react";

export class SubscriptionApiService {
    constructor() {
        this.apiBaseUrl = config.apiBaseUrl;
    }

    /**
     * Get user token from localStorage
     * @returns {string|null}
     */
    getUserToken() {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.token || null;
    }



    /**
     * Get available subscription plans
     * @returns {Promise<Array>}
     */
    async getSubscriptionPlans() {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/plans`, {
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch subscription plans: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching subscription plans:', error);
            throw error;
        }
    }

    /**
     * Get user's current subscription
     * @returns {Promise<Object|null>}
     */
    async getUserSubscriptions() {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/current`, {
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch user subscriptions: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching user subscriptions:', error);
            throw error;
        }
    }

    /**
     * Create a subscription checkout session
     * @param {string} subscriptionPlanId
     * @returns {Promise<Object>}
     */
    async createSubscriptionCheckout(subscriptionPlanId) {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/checkout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                },
                body: JSON.stringify({
                    plan_id: subscriptionPlanId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to create subscription checkout: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating subscription checkout:', error);
            throw error;
        }
    }

    /**
     * Create a new subscription (deprecated - use createSubscriptionCheckout instead)
     * @param {string} subscriptionPlanId
     * @param {string} paymentMethodId
     * @returns {Promise<Object>}
     */
    async createSubscription(subscriptionPlanId, paymentMethodId) {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/subscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                },
                body: JSON.stringify({
                    subscription_plan_id: subscriptionPlanId,
                    payment_method_id: paymentMethodId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to create subscription: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating subscription:', error);
            throw error;
        }
    }

    /**
     * Cancel a subscription
     * @param {string} subscriptionId
     * @returns {Promise<Object>}
     */
    async cancelSubscription(subscriptionId) {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/cancel/${subscriptionId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to cancel subscription: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error canceling subscription:', error);
            throw error;
        }
    }

    /**
     * Reactivate a subscription
     * @param {string} subscriptionId
     * @returns {Promise<Object>}
     */
    async reactivateSubscription(subscriptionId) {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/reactivate/${subscriptionId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to reactivate subscription: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error reactivating subscription:', error);
            throw error;
        }
    }

    /**
     * Get subscription billing history
     * @returns {Promise<Array>}
     */
    async getSubscriptionHistory() {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/billing-history`, {
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch subscription history: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching subscription history:', error);
            throw error;
        }
    }

    /**
     * Get Stripe billing portal URL
     * @returns {Promise<Object>}
     */
    async getBillingPortalUrl() {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/billing-portal`, {
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to get billing portal URL: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error getting billing portal URL:', error);
            throw error;
        }
    }

    /**
     * Preview plan change costs before committing
     * @param {string} newPlanId - The target plan ID to preview
     * @returns {Promise<Object>} Preview details with cost breakdown
     */
    async previewPlanChange(newPlanId) {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/preview-plan-change`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                },
                body: JSON.stringify({
                    new_plan_id: newPlanId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to preview plan change: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error previewing plan change:', error);
            throw error;
        }
    }

    /**
     * Initiate plan change with proper proration
     * @param {string} newPlanId - The target plan ID
     * @returns {Promise<Object>} Plan change request details
     */
    async createPlanChange(newPlanId) {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/plan-change`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                },
                body: JSON.stringify({
                    new_plan_id: newPlanId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to create plan change: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating plan change:', error);
            throw error;
        }
    }

    /**
     * Get status of plan change request
     * @param {string} planChangeId - The plan change request ID
     * @returns {Promise<Object>} Status details
     */
    async getPlanChangeStatus(planChangeId) {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/plan-change-status/${planChangeId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to get plan change status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error getting plan change status:', error);
            throw error;
        }
    }

    /**
     * Retry failed plan change payment
     * @param {string} planChangeId - The plan change request ID
     * @returns {Promise<Object>} Retry result
     */
    async retryPlanChangePayment(planChangeId) {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/retry-payment/${planChangeId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to retry payment: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error retrying payment:', error);
            throw error;
        }
    }

    /**
     * Verify subscription by checkout session ID
     * @param {string} sessionId - The Stripe checkout session ID
     * @returns {Promise<Object>} Verification result with subscription details
     */
    async verifySubscriptionSession(sessionId) {
        const token = this.getUserToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/subscriptions/verify-session/${sessionId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'User-Authorization': token
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to verify subscription session: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error verifying subscription session:', error);
            throw error;
        }
    }
}

const subscriptionApiService = new SubscriptionApiService();
export default subscriptionApiService; 